// /////////////////////////////////////////////////////////////
// [ファイル] SfaProjectDetailPage.tsx
// [概要] 営業案件詳細（推進案件機能統合 リリース1）
//        タブ＝基本情報／土地仕入／販売区画／フェーズ履歴／添付ファイル。
//        土地仕入タブは仕入契約がある案件だけに出す。契約の追加は右の「案件の構成」から行う。
//        ヘッダーにタイプ（システム判定）・フェーズ（仕入ありは仕入と売上の両方）・構成の要約を置く。
//        案件の失注は土地仕入タブの仕入フェーズの失注だけで行う（仕入契約がすべて失注になると案件も失注。2026-11-03 に
//        ヘッダーの「案件を失注にする」から一本化）。失注の表示は失注日の有無でなく、仕入契約がすべて失注かどうかで決める。
//        仕入のない区画 1 つの案件は「売上」として区画を意識させない。
//        案件本体の項目（案件名・大項目/中項目・物件所在地・レコードタイプ・金融機関・備考・担当チーム）は、
//        ヘッダーの「編集モード」で編集モードに入り、項目ごとのインライン編集（components/InlineEditField）で直す。
//        閲覧中は鉛筆も下線も出さない。保存は項目ごと（送った項目だけ更新）。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { CATEGORY_LARGE, CATEGORY_MEDIUM_BY_LARGE } from '../../constants/masterData';
import LoadingView from '../../components/LoadingView';
import { useAuth } from '../../firebase/AuthProvider';
import { useNotification } from '../../providers/NotificationProvider';
import { useConfirm } from '../../providers/ConfirmProvider';
import { useMasterData } from '../../providers/MasterDataProvider';
import ContractPanel, { ContractDialog } from '../../components/sfa/ContractPanel';
import UnitPanel from '../../components/sfa/UnitPanel';
import ProjectAttachmentsPanel from '../../components/sfa/ProjectAttachmentsPanel';
import ActivityHistoryPanel from '../../components/sfa/ActivityHistoryPanel';
import { BookingChip, OverdueChip, SfaTypeChip, UnitPhaseChip } from '../../components/sfa/SfaStructureChips';
import { InlineEdit, InlineEditModeProvider, InlineSelectField, InlineTextField } from '../../components/InlineEditField';
import { useSfaProjectV2, sfaStructureApi, fillStaffFields, isFlatProject, isProjectLost, fmtArea, fmtDate, fmtSettlement, fmtYen, fmtYm, LOST_PHASE } from '../../hooks/useSfaStructure';
import { useLostReasons } from '../../components/sfa/SfaInlineFields';

// 列が多い表は折り返さず横スクロールさせる（見出し・セルの縦折り返しを防ぐ）
const NOWRAP_TABLE_SX = { '& th, & td': { whiteSpace: 'nowrap' } };

export default function SfaProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotification();
  const { user } = useAuth();
  const operatorId = user?.email || undefined;
  const { data, loading, error, reload } = useSfaProjectV2(id);
  const { staffList } = useMasterData();
  const [tab, setTab] = useState('basic');
  // 編集モード。ヘッダーの「編集モード」で入り、編集できる項目に鉛筆と下線が出る（閲覧中は何も出さない）。保存は項目ごと
  const [editMode, setEditMode] = useState(false);
  const openEditorsRef = useRef(0); // 開いている入力欄の数（保存していない入力があるまま終えるときの確認用）
  const onEditingChange = useCallback((open: boolean) => { openEditorsRef.current += open ? 1 : -1; }, []);
  const confirm = useConfirm();
  const [addContractOpen, setAddContractOpen] = useState(false);

  // 通知 API は参照が変わるため依存に入れない（無限ループ防止）
  const notifyRef = useRef(notify); notifyRef.current = notify;
  useEffect(() => { if (error) notifyRef.current.error(error); }, [error]);

  const flat = isFlatProject(data);
  // 土地仕入から販売への流れに合わせ、土地仕入は常に基本情報の次。仕入契約が無い案件では出さない
  const hasLand = !!data && data.contracts.length > 0;
  const tabs = useMemo(() => [
    ['basic', '基本情報'],
    ...(hasLand ? [['land', '土地仕入']] : []),
    ['units', flat ? '売上' : '販売区画'],
    ['history', 'フェーズ履歴'],
    ['files', '添付ファイル'],
  ], [flat, hasLand]);
  // 最後の契約を削除して土地仕入タブが消えたときは基本情報を表示する
  const activeTab = tabs.some(([k]) => k === tab) ? tab : 'basic';

  if (loading || !data) return <LoadingView message="案件を読み込み中..." height="80vh" />;
  const p = data.project;
  const silentReload = () => reload(true);
  // 項目ごとのインライン編集の保存。送った項目だけを更新する（API は部分更新。ほかの人が同時に別の項目を直しても上書きしない）。
  // 再読込を待ってから編集を閉じ、保存後の値をすぐ表示する。失敗は通知し、入力欄に理由を残す（例外を投げ直す）
  const saveProject = async (patch: Record<string, any>, label: string) => {
    try {
      await sfaStructureApi.updateProject(p.project_id, fillStaffFields({ ...patch, operator_id: operatorId }, staffList));
    } catch (e: any) {
      notify.error(e.message);
      throw e;
    }
    notify.success(`${label}を更新しました`);
    await silentReload();
  };
  // 担当者の選択肢（旧ダイアログと同じ表記）
  const staffOptions = staffList.map((s: any) => ({ value: s.staff_id, label: `${s.staff_name}（${s.branch_name}）` }));
  // 編集モードを終える。保存していない入力欄があれば、破棄してよいか確かめる
  const finishEdit = async () => {
    if (openEditorsRef.current > 0) {
      const ok = await confirm({ title: '編集を終える', message: '保存していない入力があります。入力を破棄して編集を終えますか？', confirmText: '破棄して終える', destructive: true });
      if (!ok) return;
    }
    setEditMode(false);
  };
  // 追加ボタンから最初の契約を登録したら、再読込後に土地仕入タブを開く
  const contractAdded = () => { setTab('land'); silentReload(); };
  const addContractButton = (
    <Button size="small" startIcon={<Icons.Add />} sx={{ ...STANDARD_TEXT_BUTTON_SX, mt: 0.5 }} onClick={() => setAddContractOpen(true)}>仕入契約を追加</Button>
  );

  return (
    <InlineEditModeProvider value={{ mode: editMode ? 'edit' : 'view', onEditingChange }}>
    {/* 下に余白を取る（最下部の備考などの入力欄を開いても、画面の下端に張り付かず押しやすいように） */}
    <Box sx={{ pt: 2, pb: 10, width: '96%', margin: '0 auto', boxSizing: 'border-box' }}>
      <Button startIcon={<Icons.ArrowBack />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => navigate('/sfa/projects')}>一覧に戻る</Button>

      {/* ヘッダー */}
      {/* 1 段目は案件名と操作ボタン、2・3 段目 (フェーズ・タイプ・構成 / ID・日付) は全幅。
          編集モードでボタンが増えても狭くなるのは 1 段目だけで、下の 2 段は閲覧中と同じ幅のまま折り返さない */}
      <Paper sx={{ p: 2, mt: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>案件名：</Typography>
            <InlineTextField
              value={p.project_name} label="案件名" required editMinWidth={480}
              display={<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.4, wordBreak: 'break-word' }}>{p.project_name}</Typography>}
              onSave={(v) => saveProject({ project_name: v }, '案件名')}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {editMode ? (
              <>
                <Typography variant="caption" sx={{ color: '#607d8b' }}>下線の項目を押すと、その場で直せます</Typography>
                <Button size="small" startIcon={<Icons.Check />} sx={STANDARD_CTA_BUTTON_SX} onClick={finishEdit}>編集を終える</Button>
              </>
            ) : (
              <Button size="small" startIcon={<Icons.Edit />} sx={STANDARD_CTA_BUTTON_SX} onClick={() => setEditMode(true)}>編集モード</Button>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {p.purchase_phase ? (
            <>
              <Typography variant="caption" color="text.secondary">仕入</Typography><UnitPhaseChip phase={p.purchase_phase} purchase />
              <Typography variant="caption" color="text.secondary">売上</Typography><UnitPhaseChip phase={p.rep_phase} />
            </>
          ) : <UnitPhaseChip phase={p.rep_phase} />}
          <SfaTypeChip kind={p.project_kind} />
          {!flat && (
            <Typography variant="caption" color="text.secondary">
              {p.contract_count ? `仕入 ${p.contract_count} 契約 ／ ` : ''}区画 {p.unit_count}（受注 {p.won_count}・見込み {p.open_count}{p.idle_count ? `・販売前 ${p.idle_count}` : ''}{p.lost_count ? `・失注 ${p.lost_count}` : ''}）
            </Typography>
          )}
          {isProjectLost(p) && <Chip size="small" label={p.lost_at ? `案件の失注 ${fmtDate(p.lost_at)}` : '案件の失注'} sx={{ height: 20, fontSize: '0.72rem', bgcolor: '#ffebee', color: '#c62828' }} />}
        </Box>
        <Box sx={{ display: 'flex', columnGap: 2, rowGap: 0.25, mt: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">ID: {p.project_id}</Typography>
          {p.land_name && <Typography variant="caption" color="text.secondary">物件所在地: {p.land_name}</Typography>}
          <Typography variant="caption" color="text.secondary">作成日: {fmtDate(p.created_date)}</Typography>
          <Typography variant="caption" color="text.secondary">最終更新日: {fmtDate(p.updated_date)}</Typography>
        </Box>
      </Paper>

      {/* grid の子は minWidth: 0 を付けないと、幅の広い表に合わせて列が伸び右カラムが画面外へ押し出される */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px', lg: 'minmax(0, 1fr) 400px' }, gap: 1.5, alignItems: 'start' }}>
        <Paper sx={{ p: 1.5, minWidth: 0 }}>
          <Tabs value={activeTab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}>
            {tabs.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
          </Tabs>
          {activeTab === 'basic' && <BasicPanel data={data} flat={flat} onSave={saveProject} />}
          {activeTab === 'land' && <ContractPanel projectId={p.project_id} contracts={data.contracts} units={data.units} operatorId={operatorId} onChanged={silentReload} />}
          {activeTab === 'units' && <UnitPanel detail={data} flat={flat} operatorId={operatorId} onChanged={silentReload} />}
          {activeTab === 'history' && <HistoryPanel data={data} />}
          {activeTab === 'files' && <ProjectAttachmentsPanel projectId={p.project_id} />}
        </Paper>

        {/* 右カラム: 構成・担当・活動履歴（活動履歴は旧画面と同じく常に表示） */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          <Paper sx={{ p: 1.5, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>案件の構成</Typography>
            {flat ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  区画 1 つの案件（画面に区画を出さない）。土地を仕入れる場合は{editMode ? '下の' : '「編集モード」を押してから'}「仕入契約を追加」で登録すると、タイプが「仕入あり」に変わり「土地仕入」タブが表示されます。
                </Typography>
                {editMode && addContractButton}
              </>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">仕入契約{data.contracts.length ? '' : '（なし）'}</Typography>
                {!hasLand && editMode && <Box sx={{ pl: 0.5 }}>{addContractButton}</Box>}
                {data.contracts.map((c) => (
                  <Box key={c.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pl: 1, py: 0.25, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={() => setTab('land')}>{c.contract_name}</Typography>
                    <UnitPhaseChip phase={c.phase} purchase />
                    <Typography variant="caption" color="text.secondary">{c.parcels.filter((x) => x.status === 'active').map((x) => x.chiban).join(', ') || '筆なし'}</Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>販売区画</Typography>
                {data.units.map((u) => (
                  <Box key={u.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pl: 1, py: 0.25, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={() => setTab('units')}>{u.unit_name}</Typography>
                    <UnitPhaseChip phase={u.phase} />
                    <Typography variant="caption" color="text.secondary">{u.parcel_chiban || (u.unit_state === 'idle' ? '' : '筆未割当')}</Typography>
                  </Box>
                ))}
                {p.unassigned_parcel_count > 0 && data.units.some((u) => u.unit_state !== 'idle') && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#c62828' }}>区画に割り当てていない筆が {p.unassigned_parcel_count} 筆あります（1 筆は 1 区画にのみ割当可能）。</Typography>
                )}
              </>
            )}
          </Paper>
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>担当チーム</Typography>
            {/* 担当者はその場で変更できる（拠点・部署は選んだ担当者から自動で入る）。所有者・サポートは「（なし）」も選べる */}
            {([
              ['main_staff_user_id', p.main_staff_user_id, p.main_staff_name, 'メイン担当', undefined],
              ['owner_id', p.owner_id, p.owner_name, '所有者', '（なし）'],
              ['support_staff_id', p.support_staff_id, p.support_staff_name, 'サポート', '（なし）'],
            ] as const).map(([key, staffId, staffName, role, emptyLabel]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0 }}>{role}</Typography>
                <InlineSelectField
                  value={staffId} options={staffOptions} currentLabel={staffName || undefined} emptyLabel={emptyLabel} label={role}
                  display={<Typography variant="body2">{staffName || '-'}</Typography>}
                  onSave={(v) => saveProject({ [key]: v }, role)}
                />
              </Box>
            ))}
          </Paper>
          <ActivityHistoryPanel projectId={p.project_id} defaultStaff={p.main_staff_user_id ? { id: p.main_staff_user_id, name: p.main_staff_name || '' } : null} />
        </Box>
      </Box>

      <ContractDialog open={addContractOpen} contract={null} projectId={p.project_id} operatorId={operatorId} onClose={() => setAddContractOpen(false)} onSaved={contractAdded} />
    </Box>
    </InlineEditModeProvider>
  );
}

// --------------------------------------------------
// 基本情報
// --------------------------------------------------
function BasicPanel({ data, flat, onSave }: {
  data: NonNullable<ReturnType<typeof useSfaProjectV2>['data']>; flat: boolean;
  onSave: (patch: Record<string, any>, label: string) => Promise<void>;
}) {
  const p = data.project;
  const u0 = data.units[0];
  const lostReasons = useLostReasons(true);
  // 中項目の選択肢は大項目で決まる（大項目を変えるときは中項目と一組で直す）
  const mediumOptions = (CATEGORY_MEDIUM_BY_LARGE[p.category_large] || []).map((m: string) => ({ value: m, label: m }));
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.25 }}>
      <F label="大項目" value={
        <InlineEdit<{ large: string; medium: string }>
          value={{ large: p.category_large || '', medium: p.category_medium || '' }} label="大項目・中項目"
          display={<Typography variant="body2">{p.category_large || '-'}</Typography>}
          isEqual={(a, b) => a.large === b.large && a.medium === b.medium}
          onSave={(v) => onSave({ category_large: v.large, category_medium: v.medium }, '大項目・中項目')}
          renderEditor={({ draft, setDraft, saving }) => {
            const mediums = CATEGORY_MEDIUM_BY_LARGE[draft.large] || [];
            const selectProps = { MenuProps: { disableScrollLock: true } };
            return (
              // 入力欄のラベルが項目の見出しに重ならないよう、上に少し空ける
              <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
                <TextField select size="small" label="大項目" fullWidth autoFocus value={draft.large} disabled={saving} slotProps={{ select: selectProps }}
                  onChange={(e) => setDraft({ large: e.target.value, medium: '' })}>
                  {[draft.large, ...CATEGORY_LARGE].filter((v, i, a) => v && a.indexOf(v) === i).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="中項目" fullWidth value={draft.medium} disabled={saving || !draft.large} slotProps={{ select: selectProps }}
                  onChange={(e) => setDraft({ ...draft, medium: e.target.value })}>
                  <MenuItem value="">（なし）</MenuItem>
                  {[draft.medium, ...mediums].filter((v, i, a) => v && a.indexOf(v) === i).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Box>
            );
          }}
        />
      } />
      <F label="中項目" value={
        <InlineSelectField
          value={p.category_medium} options={mediumOptions} emptyLabel="（なし）" label="中項目" disabled={!p.category_large}
          onSave={(v) => onSave({ category_medium: v }, '中項目')}
        />
      } />
      <F label="タイプ（システム判定）" span2 value={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <SfaTypeChip kind={p.project_kind} />
          <Typography variant="caption" color="text.secondary">{p.contract_count ? `仕入契約 ${p.contract_count} 件 ／ ` : '仕入契約なし ／ '}販売区画 {p.unit_count} 件。人が選ぶ区分は持たず、仕入契約の有無から判定（画面からは変更不可）</Typography>
        </Box>
      } />
      <F label="物件所在地" span2 value={<InlineTextField value={p.land_name} label="物件所在地" onSave={(v) => onSave({ land_name: v }, '物件所在地')} />} />
      {flat ? (
        <>
          <F label="顧客" value={u0.customers.map((c) => c.display_name).join('、') || '-'} />
          <F label="受注予定年月 → 受注確定日" strong value={<>{fmtYm(u0.expected_order_ym)} → {fmtDate(u0.order_confirmed_date)} <OverdueChip show={u0.is_overdue} />{u0.plan_diff_months !== null && <Typography component="span" variant="caption" color="text.secondary">（予定比 {u0.plan_diff_months === 0 ? '同月' : `${u0.plan_diff_months > 0 ? '+' : ''}${u0.plan_diff_months} か月`}）</Typography>}</>} />
          <F label="売上" value={fmtYen(u0.sales_amount)} />
          <F label="業績見込み 計上月" value={u0.unit_state === 'idle' ? '販売前（集計対象外）' : u0.unit_state === 'lost' ? '失注' : <BookingChip ym={u0.booking_ym} confirmed={!!u0.order_confirmed_date} />} />
        </>
      ) : (
        <>
          <F label="仕入フェーズ（契約ごと）" strong value={data.contracts.length ? data.contracts.map((c) => (
            <Box key={c.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>{c.contract_name}: <UnitPhaseChip phase={c.phase} purchase /><Typography variant="caption">{c.contract_date ? `契約 ${fmtDate(c.contract_date)}` : '契約日 未定'} ／ {fmtSettlement(c.settlement_date)}</Typography></Box>
          )) : '仕入契約なし'} />
          <F label="受注確定日（区画ごと）" strong value={data.units.map((u) => (
            <Box key={u.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>{u.unit_name}: {u.order_confirmed_date ? fmtDate(u.order_confirmed_date) : <Typography variant="caption" color="text.secondary">{u.expected_order_ym ? `予定 ${fmtYm(u.expected_order_ym)}` : '未定'}</Typography>}<OverdueChip show={u.is_overdue} /></Box>
          ))} />
          <F label="取得原価（仕入契約の合計）" value={fmtYen(p.purchase_total)} />
          <F label="売上高（区画の合計）" value={fmtYen(p.sales_total)} />
          <F label="敷地面積（現行の筆の合計・自動）" value={`${fmtArea(p.active_area)}（当初 ${p.parcel_count_before ?? '-'} 筆 → 現在 ${p.parcel_count_after ?? '-'} 筆）`} />
          <F label="業績見込み（区画単位・計上月）" value={data.units.filter((u) => u.unit_state === 'open' || u.unit_state === 'won').map((u) => (
            <Box key={u.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>{u.unit_name} <BookingChip ym={u.booking_ym} confirmed={!!u.order_confirmed_date} /></Box>
          )).concat(data.units.some((u) => u.unit_state === 'open' || u.unit_state === 'won') ? [] : [<Typography key="none" variant="caption" color="text.secondary">計上対象の区画なし（販売前・失注は対象外）</Typography>])} />
        </>
      )}
      <F label="レコードタイプ" value={<InlineTextField value={p.record_type} label="レコードタイプ" onSave={(v) => onSave({ record_type: v }, 'レコードタイプ')} />} />
      <F label="金融機関" value={<InlineTextField value={p.financial_institution} label="金融機関" onSave={(v) => onSave({ financial_institution: v }, '金融機関')} />} />
      <F label="備考" span2 value={<InlineTextField value={p.remarks} label="備考" multiline onSave={(v) => onSave({ remarks: v }, '備考')} />} />
      {isProjectLost(p) && (
        <F label="案件の失注（仕入契約がすべて失注）" span2 value={
          [p.lost_at ? fmtDate(p.lost_at) : '', reasonName(lostReasons, p.lost_reason_category), p.lost_reason_detail || ''].filter(Boolean).join(' ／ ') || '失注'
        } />
      )}
    </Box>
  );
}

// 基本情報の 1 項目（見出しと値）。BasicPanel の中で定義すると描き直しのたびに別の部品として作り直され、
// 開いている入力欄が閉じて入力が消えるため、外に置く
function F({ label, value, strong, span2 }: { label: string; value: React.ReactNode; strong?: boolean; span2?: boolean }) {
  return (
    <Box sx={{ gridColumn: span2 ? 'span 2' : undefined }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: strong ? 600 : 400 }}>{value}</Typography>
    </Box>
  );
}

// 失注理由のコードを名前にする（マスタに無いコードはそのまま出す）
const reasonName = (reasons: { reason_code: string; reason_name: string }[], code: string | null | undefined) =>
  (code ? reasons.find((r) => r.reason_code === code)?.reason_name || code : '');

// --------------------------------------------------
// フェーズ履歴（仕入契約ごとの仕入フェーズ＋区画ごとの売上フェーズ。予定年月の変更も記録）
// --------------------------------------------------
const UNIT_KIND_LABEL: Record<string, string> = { phase: '売上フェーズ', expected_ym: '受注予定年月', lost_all: '案件の失注（同時失注）' };
const CONTRACT_KIND_LABEL: Record<string, string> = { phase: '仕入フェーズ', legacy: '仕入フェーズ（旧画面）', lost_all: '案件の失注（仕入）' };

function HistoryPanel({ data }: { data: NonNullable<ReturnType<typeof useSfaProjectV2>['data']> }) {
  const lostReasons = useLostReasons(true);
  const rows = useMemo(() => [
    // lost_all で失注から戻した行は、仕入契約を失注から戻したことによる取り消し
    ...data.phase_history.map((h) => ({ ...h, key: `u${h.history_id}`, target: h.unit_name || '', purchase: false,
      kind: h.change_kind === 'lost_all' && h.phase_before === LOST_PHASE ? '案件の失注の取り消し（同時に戻す）' : UNIT_KIND_LABEL[h.change_kind] || h.change_kind })),
    ...(data.contract_phase_history || []).map((h) => ({
      ...h, key: `c${h.history_id}`, target: `仕入 ${h.contract_name || ''}`, purchase: true, kind: CONTRACT_KIND_LABEL[h.change_kind] || h.change_kind, expected_ym_before: null, expected_ym_after: null,
    })),
  ].sort((a, b) => (a.changed_at < b.changed_at ? 1 : a.changed_at > b.changed_at ? -1 : 0)), [data]);
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">フェーズ変更履歴はまだありません。</Typography>;
  return (
    <Box sx={{ overflowX: 'auto' }}><Table size="small" sx={NOWRAP_TABLE_SX}>
      <TableHead><TableRow><TableCell>日時</TableCell><TableCell>対象</TableCell><TableCell>種別</TableCell><TableCell>変更</TableCell><TableCell>変更者</TableCell></TableRow></TableHead>
      <TableBody>
        {rows.map((h) => (
          <TableRow key={h.key}>
            <TableCell>{h.changed_at.slice(0, 16).replace('T', ' ')}</TableCell>
            <TableCell>{h.target}</TableCell>
            <TableCell>{h.kind}</TableCell>
            <TableCell>
              {h.change_kind === 'expected_ym'
                ? `${fmtYm(h.expected_ym_before)} → ${fmtYm(h.expected_ym_after)}`
                : <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}><UnitPhaseChip phase={h.phase_before || undefined} purchase={h.purchase} /> → <UnitPhaseChip phase={h.phase_after || undefined} purchase={h.purchase} />{h.lost_reason_category && <Typography variant="caption" color="text.secondary">（{reasonName(lostReasons, h.lost_reason_category)}{h.lost_reason_detail ? ` ${h.lost_reason_detail}` : ''}）</Typography>}</Box>}
            </TableCell>
            <TableCell>{h.changed_by || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table></Box>
  );
}
